import { useState } from "react"
import {
  LayoutDashboard,
  Users,
  UserCheck,
  MessageSquare,
  QrCode,
  Mail,
  ImageIcon,
  Link,
  Share2,
  Settings,
  Bell,
  Shield,
  Library,
  Speaker,
  Contact,
  Gamepad2,
  Wine,
  HeartHandshake,
  Hand,
  Calendar,
  ChevronDown,
  ChevronRight,
  Book,
} from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "../../components/ui/sidebar"
import ImagesDev from '../../api/images';

interface AdminSidebarProps {
  activeSection: string
  onSectionChange: (section: string) => void
}

const menuItems = [
  {
    title: "Overview",
    items: [
      { id: "dashboard", title: "Dashboard", icon: LayoutDashboard },
      { id: "healing-pavilion", title: "Healing Pavilion", icon: Wine, subItems: [
        { id: "healing-pavilion/healing-dashboard", title: "Dashboard", icon: LayoutDashboard },
        { id: "healing-pavilion/availability", title: "Availability Management", icon: Calendar },
        { id: "healing-pavilion/bookings", title: "Bookings Management", icon: Users },
        { id: "healing-pavilion/settings", title: "Settings & Configuration", icon: Settings },
      ]},
    ],
  },
  {
    title: "User Management",
    items: [
      { id: "users", title: "Users & Admins", icon: Users },
      { id: "registrants", title: "Portal Registrants", icon: UserCheck },
      { id: "bio-data-form", title: "Bio Data Form", icon: Contact },
      {id: "partner-form", title: "Partner Form", icon: HeartHandshake},
      {id: "volunteer-form", title: "Volunteer Form", icon: Hand},
      {id: "biblestudy-masterclass", title: "Biblestudy Masterclass", icon: Book}
    ],
  },
  {
    title: "Content Management",
    items: [
      { id: "comments", title: "Comments & Feedback", icon: MessageSquare },
      { id: "slider", title: "Homepage Slider", icon: ImageIcon },
      { id: "library-settings", title: "Library Settings", icon: Library },
      { id: "daily-declaration", title: "Daily Declaration", icon: Speaker },
      { id: "linktree", title: "LinkTree Builder", icon: Link },
    ],
  },
  {
    title: "Lucrative",
    items: [
      { id: "weekly-game", title: "Weekly Game", icon: Gamepad2 },
    ],
  },
  {
    title: "Tools & Utilities",
    items: [
      { id: "qr-generator", title: "QR Code Generator", icon: QrCode },
      { id: "email-tools", title: "Email & Messaging", icon: Mail },
    ],
  },
  {
    title: "Settings",
    items: [
      { id: "social-seo", title: "Social Media & SEO", icon: Share2 },
      { id: "site-settings", title: "General Site Settings", icon: Settings },
      { id: "notifications", title: "Notifications", icon: Bell },
      { id: "security", title: "Security Logs", icon: Shield },
    ],
  },
]

export function AdminSidebar({ activeSection, onSectionChange }: AdminSidebarProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const handleItemClick = (itemId: string, hasSubItems: boolean) => {
    if (hasSubItems) {
      toggleExpanded(itemId)
    } else {
      onSectionChange(itemId)
    }
  }

  return (
    <Sidebar>
      <SidebarHeader className="p-4 bg-gray-600 flex flex-row items-center gap-3">
        <img className="w-12 h-12 rounded-lg transition-transform duration-300 hover:scale-105" src={ImagesDev.logo} alt="TSO Logo" />
        <h2 className="text-lg font-semibold text-white">TSO Admin</h2>
      </SidebarHeader>
      <SidebarContent>
        {menuItems.map((group) => (
          <SidebarGroup key={group.title}>
            <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item:any) => {
                  const hasSubItems = item.subItems && item.subItems.length > 0
                  const isExpanded = expandedItems.has(item.id)
                  const isParentActive = hasSubItems && item.subItems?.some((sub:any) => sub.id === activeSection)

                  return (
                    <div key={item.id}>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          isActive={!hasSubItems && activeSection === item.id || isParentActive}
                          onClick={() => handleItemClick(item.id, hasSubItems)}
                        >
                          <item.icon className="h-4 w-4" />
                          <span className="flex-1 text-left">{item.title}</span>
                          {hasSubItems && (
                            isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>

                      {hasSubItems && isExpanded && (
                        <div className="ml-2 mt-1 space-y-1">
                          {item.subItems?.map((subItem:any) => (
                            <SidebarMenuItem key={subItem.id}>
                              <SidebarMenuButton
                                isActive={activeSection === subItem.id}
                                onClick={() => onSectionChange(subItem.id)}
                                // indent
                              >
                                <subItem.icon className="h-4 w-4" />
                                <span>{subItem.title}</span>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  )
}
